import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Drawer, Button, Tag, Typography, Divider, Progress, Table, Popconfirm,
  message, Badge, Space, Tooltip, Modal, Input,
} from "antd";
import {
  CalendarOutlined, ClockCircleOutlined, TeamOutlined, DollarOutlined,
  PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined,
  AppstoreOutlined, InfoCircleOutlined, CoffeeOutlined, LayoutOutlined, LockOutlined,
  PrinterOutlined, FilePdfOutlined, FileTextOutlined,
} from "@ant-design/icons";
import {
  exportEventReservationsPDF,
  exportEventReservationsCSV,
  printEventReservations,
} from "../../../../utils/helpers/exportUtils";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { VenueEvent } from "../../../../utils/api/venueEventsAPI";
import { getEventReservations } from "../../../../utils/api/venueEventsAPI";
import type { Reservation } from "../../../../utils/api/reservationsAPI";
import { deleteReservation, updateReservation } from "../../../../utils/api/reservationsAPI";
import EventTableMap from "../EventTableMap/EventTableMap";
import EventFloorPlan from "../EventFloorPlan/EventFloorPlan";
import "./VenueEventDetail.scss";

const { Title, Text } = Typography;

const EVENT_TYPE_LABELS: Record<string, string> = {
  concert: "Concert", standup: "Stand-up", band: "Live Band",
  private: "Private Event", sports: "Sports", other: "Other",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "orange", confirmed: "blue", paid: "green",
  waitlisted: "purple", cancelled: "red", "no-show": "default", attended: "cyan",
};

const PAYMENT_COLORS: Record<string, string> = {
  unpaid: "red", paid: "green", partially_paid: "orange",
};

type Props = {
  event: VenueEvent | null;
  open: boolean;
  onClose: () => void;
  onEdit?: (event: VenueEvent) => void;
  onDelete?: (id: string) => void;
  onAddReservation?: (event: VenueEvent, tableNumber?: number, reservations?: Reservation[]) => void;
  onEditReservation?: (reservation: Reservation) => void;
  refreshKey?: number;
};

const VenueEventDetail: React.FC<Props> = ({
  event, open, onClose, onEdit, onDelete, onAddReservation, onEditReservation, refreshKey,
}) => {
  const { t, i18n } = useTranslation();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [approveModal, setApproveModal] = useState<Reservation | null>(null);
  const [waiterName, setWaiterName] = useState("");
  const [approving, setApproving] = useState(false);
  const waiterInputRef = useRef<any>(null);
  const [floorPlanOpen, setFloorPlanOpen] = useState(false);
  const [localTableLayout, setLocalTableLayout] = useState<{ number: number; x: number; y: number }[] | null | undefined>(undefined);
  const [localFloorPlanBg, setLocalFloorPlanBg] = useState<"grid" | "image" | undefined>(undefined);

  const fetchReservations = useCallback(async () => {
    if (!event) return;
    setLoading(true);
    try {
      const res = await getEventReservations(event._id);
      setReservations(res.data);
    } catch {
      message.error(t("Failed to load reservations"));
    } finally {
      setLoading(false);
    }
  }, [event?._id]);

  useEffect(() => {
    if (open && event) fetchReservations();
    else setReservations([]);
  }, [open, event?._id, refreshKey]);

  const handleDeleteReservation = async (id: string) => {
    try {
      await deleteReservation(id);
      message.success(t("Reservation deleted"));
      fetchReservations();
    } catch {
      message.error(t("Failed to delete reservation"));
    }
  };

  const handleApprove = async () => {
    if (!approveModal) return;
    if (!waiterName.trim()) {
      message.warning(t("Please enter the waiter name"));
      waiterInputRef.current?.focus();
      return;
    }
    setApproving(true);
    try {
      await updateReservation(approveModal._id, { status: "confirmed", waiterName: waiterName.trim() });
      message.success(t("Reservation approved"));
      setApproveModal(null);
      setWaiterName("");
      fetchReservations();
    } catch {
      message.error(t("Failed to approve reservation"));
    } finally {
      setApproving(false);
    }
  };

  if (!event) return null;

  const availableSpots = event.totalCapacity - event.reservedCount;
  const fillPercent = Math.round((event.reservedCount / event.totalCapacity) * 100);
  const isFull = availableSpots <= 0;

  const totalRevenue = reservations
    .filter((r) => r.paymentStatus === "paid")
    .reduce((sum, r) => sum + (r.amountPaid || 0), 0);

  const columns = [
    {
      title: t("Guest"),
      dataIndex: "guestName",
      render: (name: string, r: Reservation) => (
        <div>
          <div style={{ fontWeight: 600 }}>{name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.guestPhone}</Text>
        </div>
      ),
    },
    {
      title: t("Table"),
      dataIndex: "eventTableNumber",
      width: 70,
      render: (v: number | null) => v ? <Tag color="blue">{v}</Tag> : <Text type="secondary">-</Text>,
    },
    {
      title: t("Tickets"),
      dataIndex: "ticketsCount",
      width: 70,
      render: (v: number) => v || 1,
    },
    {
      title: t("Status"),
      dataIndex: "status",
      width: 110,
      render: (s: string) => (
        <Tag color={STATUS_COLORS[s] || "default"}>{t(s.charAt(0).toUpperCase() + s.slice(1))}</Tag>
      ),
    },
    {
      title: t("Payment"),
      dataIndex: "paymentStatus",
      width: 120,
      render: (s: string, r: Reservation) => (
        <Tag color={PAYMENT_COLORS[s || "unpaid"] || "default"}>
          {s === "paid" ? `${r.amountPaid} ${event.currency}` : t(s === "partially_paid" ? "Partial" : "Unpaid")}
        </Tag>
      ),
    },
    {
      title: "",
      width: 110,
      render: (_: any, r: Reservation) => (
        <Space>
          {r.status === "pending" && (
            <Tooltip title={t("Approve ticket pickup")}>
              <Button
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => { setApproveModal(r); setWaiterName(""); }}
                style={{ background: "#059669", borderColor: "#059669" }}
              />
            </Tooltip>
          )}
          {onEditReservation && (
            <Tooltip title={t("Edit")}>
              <Button size="small" icon={<EditOutlined />} onClick={() => onEditReservation(r)} />
            </Tooltip>
          )}
          <Popconfirm
            title={t("Delete this reservation?")}
            onConfirm={() => handleDeleteReservation(r._id)}
            okText={t("Delete")} okButtonProps={{ danger: true }} cancelText={t("Cancel")}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
    <Drawer
      title={
        <div className="ved-header">
          <span className="ved-header__title">{event.title}</span>
          <Tag color={event.status === "published" ? "green" : event.status === "cancelled" ? "red" : "default"}>
            {t(event.status.charAt(0).toUpperCase() + event.status.slice(1))}
          </Tag>
        </div>
      }
      open={open}
      onClose={onClose}
      placement="right"
      width={600}
      className="venue-event-detail-drawer"
      footer={
        (onEdit || onDelete) ? (
          <div className="ved-footer">
            {onEdit && (
              <Button icon={<EditOutlined />} onClick={() => onEdit(event)}>{t("Edit Event")}</Button>
            )}
            {onDelete && (
              <Popconfirm
                title={t("Delete this event and cancel all reservations?")}
                onConfirm={() => { onDelete(event._id); onClose(); }}
                okText={t("Delete")} okButtonProps={{ danger: true }} cancelText={t("Cancel")}
              >
                <Button danger icon={<DeleteOutlined />}>{t("Delete")}</Button>
              </Popconfirm>
            )}
          </div>
        ) : null
      }
    >
      <div className="ved-body">
        {/* Event info */}
        <div className="ved-info">
          <div className="ved-info__row">
            <CalendarOutlined />
            <span>
              {new Intl.DateTimeFormat(i18n.language, {
                weekday: "long", day: "numeric", month: "long", year: "numeric",
              }).format(new Date(event.date))}
            </span>
          </div>
          <div className="ved-info__row">
            <ClockCircleOutlined />
            <span>{event.time}{event.endTime ? ` – ${event.endTime}` : ""}</span>
          </div>
          <div className="ved-info__row">
            <TeamOutlined />
            <span>{t(EVENT_TYPE_LABELS[event.type] || event.type)}</span>
          </div>
          {event.ticketPrice > 0 && (
            <div className="ved-info__row">
              <DollarOutlined />
              <span>{event.ticketPrice} {event.currency} {t("per ticket")}</span>
            </div>
          )}
          {event.minAdvanceBookingDays != null && event.minAdvanceBookingDays > 0 && (
            <div className="ved-info__row">
              <InfoCircleOutlined />
              <span>{t("Must book at least")} <strong>{event.minAdvanceBookingDays}</strong> {t("days in advance")}</span>
            </div>
          )}
          {event.description && (
            <p className="ved-info__desc">{event.description}</p>
          )}
        </div>

        <Divider />

        {/* Capacity */}
        <div className="ved-capacity">
          {event.isLocked ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#fef3c7", borderRadius: 8, border: "1px solid #fcd34d" }}>
              <LockOutlined style={{ color: "#d97706", fontSize: 18 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: "#92400e" }}>{t("Locked event")}</div>
                <div style={{ fontSize: 13, color: "#78350f" }}>
                  {event.lockedGuestCount} {t("guests pre-filled")}
                  {event.lockedPrice != null && (
                    <span style={{ marginLeft: 12 }}>
                      · {event.lockedPrice} {event.currency}
                      {" "}{event.lockedPriceType === "per_person" ? t("per person") : t("flat")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="ved-capacity__header">
                <Title level={5} style={{ margin: 0 }}>{t("Capacity")}</Title>
                <Text type={isFull ? "danger" : "secondary"}>
                  {event.reservedCount} / {event.totalCapacity} {t("reserved")}
                </Text>
              </div>
              <Progress
                percent={fillPercent}
                status={isFull ? "exception" : fillPercent >= 80 ? "active" : "normal"}
                strokeColor={fillPercent >= 80 ? "#f59e0b" : "#10b981"}
              />
              {totalRevenue > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("Collected")}: <strong>{totalRevenue} {event.currency}</strong>
                </Text>
              )}
            </>
          )}
        </div>

        {/* Table map — only shown if event has a table plan */}
        {event.totalTables != null && event.totalTables > 0 && (
          <>
            <Divider />
            <div className="ved-tables">
              <div className="ved-tables__header">
                <AppstoreOutlined />
                <Title level={5} style={{ margin: 0 }}>{t("Tables")}</Title>
                <Button
                  size="small"
                  icon={<LayoutOutlined />}
                  onClick={() => setFloorPlanOpen(true)}
                  style={{ marginLeft: "auto", marginRight: 8 }}
                >
                  {t("Floor Plan")}
                </Button>
                <div className="ved-tables__summary">
                  <span className="ved-tables__stat ved-tables__stat--total">
                    {event.totalTables} {t("total")}
                  </span>
                  <span className="ved-tables__stat ved-tables__stat--occupied">
                    {event.reservedTables ?? 0} {t("reserved")}
                  </span>
                  <span className={`ved-tables__stat ved-tables__stat--free${(event.freeTables ?? 0) === 0 ? " ved-tables__stat--zero" : ""}`}>
                    {event.freeTables ?? 0} {t("free")}
                  </span>
                </div>
              </div>
              <EventTableMap
                totalTables={event.totalTables}
                reservations={reservations}
                canAdd={!!(onAddReservation && event.status !== "cancelled")}
                onSelectFreeTable={(tableNum) =>
                  onAddReservation?.(event, tableNum, reservations)
                }
                onEditReservation={onEditReservation}
              />
            </div>
          </>
        )}

        <Divider />

        {/* Reservations */}
        {!event.isLocked && (
          <div className="ved-reservations">
            <div className="ved-reservations__header">
              <Title level={5} style={{ margin: 0 }}>{t("Reservations")} <Badge count={reservations.length} /></Title>
              <Space size={4}>
                {reservations.length > 0 && (
                  <>
                    <Tooltip title={t("Print")}>
                      <Button
                        size="small"
                        icon={<PrinterOutlined />}
                        onClick={() => printEventReservations(event, reservations)}
                      />
                    </Tooltip>
                    <Tooltip title={t("Export PDF")}>
                      <Button
                        size="small"
                        icon={<FilePdfOutlined />}
                        onClick={() => exportEventReservationsPDF(event, reservations)}
                      />
                    </Tooltip>
                    <Tooltip title={t("Export Excel")}>
                      <Button
                        size="small"
                        icon={<FileTextOutlined />}
                        onClick={() => exportEventReservationsCSV(event, reservations)}
                      />
                    </Tooltip>
                  </>
                )}
                {onAddReservation && !isFull && event.status !== "cancelled" && (
                  <Button
                    type="primary" size="small" icon={<PlusOutlined />}
                    onClick={() => onAddReservation(event, undefined, reservations)}
                  >
                    {t("Add")}
                  </Button>
                )}
              </Space>
            </div>
            <Table
              dataSource={reservations}
              columns={columns}
              rowKey="_id"
              loading={loading}
              size="small"
              pagination={false}
              locale={{ emptyText: t("No reservations") }}
              style={{ marginTop: 12 }}
            />
          </div>
        )}
      </div>
    </Drawer>

    {/* Floor plan */}
    {event.totalTables && floorPlanOpen && (
      <EventFloorPlan
        open={floorPlanOpen}
        onClose={() => setFloorPlanOpen(false)}
        event={{
          ...event,
          ...(localTableLayout !== undefined ? { tableLayout: localTableLayout } : {}),
          ...(localFloorPlanBg !== undefined ? { floorPlanBg: localFloorPlanBg } : {}),
        }}
        reservations={reservations}
        onRefresh={(savedLayout, savedBg) => {
          fetchReservations();
          if (savedLayout !== undefined) setLocalTableLayout(savedLayout);
          if (savedBg !== undefined) setLocalFloorPlanBg(savedBg);
        }}
        canEdit={!!onEdit}
      />
    )}

    {/* Approve ticket pickup modal */}
    <Modal
      open={!!approveModal}
      title={
        <Space>
          <CheckCircleOutlined style={{ color: "#059669" }} />
          {t("Approve Ticket Pickup")}
        </Space>
      }
      onCancel={() => { setApproveModal(null); setWaiterName(""); }}
      onOk={handleApprove}
      okText={t("Confirm Approval")}
      okButtonProps={{ loading: approving, style: { background: "#059669", borderColor: "#059669" } }}
      cancelText={t("Cancel")}
      afterOpenChange={(open) => { if (open) waiterInputRef.current?.focus(); }}
      destroyOnClose
    >
      {approveModal
        ? (
        <div style={{ paddingTop: 8 }}>
          <p style={{ marginBottom: 16, color: "#374151" }}>
            <strong>{approveModal.guestName}</strong> — {approveModal.ticketsCount || 1}{" "}
            {approveModal.ticketsCount === 1 ? t("ticket") : t("tickets")}
            {approveModal.eventTableNumber ? `, ${t("Table")} ${approveModal.eventTableNumber}` : ""}
          </p>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13 }}>
            <CoffeeOutlined style={{ marginRight: 6, color: "#7c3aed" }} />
            {t("Waiter Name")} *
          </label>
          <Input
            ref={waiterInputRef}
            placeholder={t("Enter waiter name")}
            value={waiterName}
            onChange={(e) => setWaiterName(e.target.value)}
            onPressEnter={handleApprove}
            size="large"
          />
        </div>
        ) : null}
    </Modal>
    </>
  );
};

export default VenueEventDetail;
